/**
 * The locale + currency registry — the single list every switcher reads.
 *
 * CONFIG, NOT CONSTANTS. Both arrays are the whole configuration surface for a
 * game: trim `LOCALES` to one entry and the game is single-language, trim
 * `CURRENCIES` to one and it is single-currency. Nothing else needs touching —
 * the switchers disappear on their own (see `hasLocaleChoice` /
 * `hasCurrencyChoice`), because a control that offers one option is a lie.
 * Everything downstream reads these arrays, never a hardcoded locale or symbol,
 * so this file is the only thing a new game edits.
 *
 * Identifiers are the underscore form (`en_US`) because that is what the task,
 * the JSON filenames and the `translations.locale` column all use. `tag` is the
 * BCP-47 form the platform's `Intl` APIs need (`en-US`) — never hand `Intl` a
 * raw identifier.
 *
 * Swedish is `sv_SE`, not the `sv_SV` written on the ticket: `SV` is not a
 * country code and `Intl` rejects `sv-SV`.
 */
export const LOCALES = [
  { id: 'en_US', tag: 'en-US', label: 'English',   endonym: 'English'   },
  { id: 'de_DE', tag: 'de-DE', label: 'German',    endonym: 'Deutsch'   },
  { id: 'pl_PL', tag: 'pl-PL', label: 'Polish',    endonym: 'Polski'    },
  { id: 'no_NO', tag: 'nb-NO', label: 'Norwegian', endonym: 'Norsk'     },
  { id: 'sv_SE', tag: 'sv-SE', label: 'Swedish',   endonym: 'Svenska'   },
  { id: 'hu_HU', tag: 'hu-HU', label: 'Hungarian', endonym: 'Magyar'    },
] as const;

export type LocaleId = (typeof LOCALES)[number]['id'];

/** The tongue every other locale falls back to, and the only one bundled. */
export const DEFAULT_LOCALE: LocaleId = 'en_US';

export const LOCALE_IDS = LOCALES.map((l) => l.id) as readonly LocaleId[];

/** False on a single-language game — the switcher must not render at all. */
export const hasLocaleChoice = LOCALES.length > 1;

export function isLocaleId(v: unknown): v is LocaleId {
  return typeof v === 'string' && (LOCALE_IDS as readonly string[]).includes(v);
}

/** BCP-47 tag for `Intl` — falls back to the default locale's tag. */
export function localeTag(id: string): string {
  return LOCALES.find((l) => l.id === id)?.tag
    ?? LOCALES.find((l) => l.id === DEFAULT_LOCALE)!.tag;
}

/**
 * Best guess from the browser, used only when the player has never chosen.
 * Matches on the full tag first (`de-DE`), then the bare language (`de`), so a
 * visitor with `de-AT` still lands on German rather than English.
 */
export function detectLocale(langs: readonly string[] = navigator.languages ?? []): LocaleId {
  for (const raw of langs) {
    const want = raw.toLowerCase();
    const exact = LOCALES.find((l) => l.tag.toLowerCase() === want);
    if (exact) return exact.id;
    const lang = want.split('-')[0];
    const byLang = LOCALES.find((l) => l.tag.toLowerCase().split('-')[0] === lang);
    if (byLang) return byLang.id;
  }
  return DEFAULT_LOCALE;
}

// ── Currency ────────────────────────────────────────────────────────────────
// NOTE: this is the *display* currency. The charge currency is a separate,
// server-side concern — see `money.ts` and `topup.service.ts`.

export const CURRENCIES = [
  { code: 'GBP', symbol: '£', label: 'British pound' },
  { code: 'EUR', symbol: '€', label: 'Euro'          },
  { code: 'USD', symbol: '$', label: 'US dollar'     },
] as const;

export type CurrencyCode = (typeof CURRENCIES)[number]['code'];

export const CURRENCY_CODES = CURRENCIES.map((c) => c.code) as readonly CurrencyCode[];

/** The currency a player sees before choosing — the first entry, by convention. */
export const DEFAULT_CURRENCY: CurrencyCode = CURRENCIES[0].code;

/** False on a single-currency game — the switcher must not render at all. */
export const hasCurrencyChoice = CURRENCIES.length > 1;

export function isCurrencyCode(v: unknown): v is CurrencyCode {
  return typeof v === 'string' && (CURRENCY_CODES as readonly string[]).includes(v);
}
